/**
 * "Shuffling the discard back in."
 *
 * Without this the deck simply refills and a player who was watching the draw
 * pile count sees it jump from nothing back to forty-odd with no explanation.
 * The moment is worth showing: it is a real thing that happens at a real table,
 * and at a real table everybody watches it.
 *
 * Driven by a **counter** in the game view, not a flag. A flag is true for one
 * tick and a client that re-rendered a moment later would miss it entirely; a
 * counter that went up is still up when you look.
 */

import { useEffect, useRef, useState } from 'react';

export function Shuffling({ count }: { count: number }) {
  const [showing, setShowing] = useState(false);
  const previous = useRef<number | null>(null);

  useEffect(() => {
    // The first value seen is the baseline, not an event - otherwise every
    // client that joins a room mid-game announces a shuffle that already
    // happened, possibly long ago.
    if (previous.current === null) {
      previous.current = count;
      return undefined;
    }
    if (count <= previous.current) return undefined;

    previous.current = count;
    setShowing(true);
    const id = window.setTimeout(() => setShowing(false), 2200);
    return () => window.clearTimeout(id);
  }, [count]);

  if (!showing) return null;

  return (
    <div className="shuffling" role="status">
      <span className="shuffling-cards" aria-hidden="true">
        <span className="shuffling-card" />
        <span className="shuffling-card" />
        <span className="shuffling-card" />
      </span>
      <span>Shuffling the discards back in…</span>
    </div>
  );
}
